import type { User } from '@api/models';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  type BookingData,
  type BookingFilters,
  type BookingQueryResult,
  getUnifiedBookings
} from '@website/services/bookings.service';

export type BookingsFilters = BookingFilters;
export type BookingsQueryResult = BookingQueryResult;

export interface UseBookingsQueryReturn {
  data?: BookingsQueryResult;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isFetching: boolean;

  // Keep these for backward compatibility but they're no-ops now
  fetchNextPage: () => Promise<any>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

/**
 * Simplified React Query hook using the unified booking service
 * Fetches ALL bookings in a single call and filters client-side
 *
 * Automatically polls every 5 seconds if any booking has PROCESSING_PAYMENT status
 * (for the rare case where user navigates away during payment processing)
 */
export const useBookingsQuery = (
  user: User | null,
  filters: BookingFilters = {},
  enabled = true
): UseBookingsQueryReturn => {
  const bookingsQuery = useQuery({
    queryKey: ['bookings', user?.id, user?.isProfessional, filters.activityId, filters.locale, filters.menteeClientId],
    queryFn: async () => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const userType = user.isProfessional ? 'professional' : 'user';
      const queryFilters: BookingFilters = {
        activityId: filters.activityId,
        locale: filters.locale
      };

      return getUnifiedBookings(userType, queryFilters);
    },
    enabled: !!user && enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('404')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
  });

  // Check if any booking has PROCESSING_PAYMENT status - if so, poll every 5 seconds
  const hasProcessingPayment = useMemo(() => {
    if (!bookingsQuery.data) return false;
    const allBookings = [
      ...bookingsQuery.data.upcoming,
      ...bookingsQuery.data.past,
      ...bookingsQuery.data.pendingPayment,
      ...bookingsQuery.data.expired
    ];
    return allBookings.some((booking: BookingData) => booking.paymentStatus === 'PROCESSING_PAYMENT');
  }, [bookingsQuery.data]);

  // Use a separate query config for polling when needed
  const pollingQuery = useQuery({
    queryKey: ['bookings', user?.id, user?.isProfessional, filters.activityId, filters.locale, 'polling'],
    queryFn: async () => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const userType = user.isProfessional ? 'professional' : 'user';
      const queryFilters: BookingFilters = {
        activityId: filters.activityId,
        locale: filters.locale
      };

      return getUnifiedBookings(userType, queryFilters);
    },
    enabled: !!user && enabled && hasProcessingPayment,
    refetchInterval: hasProcessingPayment ? 5000 : false, // Poll every 5 seconds if processing
    staleTime: 0, // Always consider stale when polling
    gcTime: 0
  });

  // Use polling data if available and polling is active, otherwise use main query
  const activeQuery = hasProcessingPayment && pollingQuery.data ? pollingQuery : bookingsQuery;

  // NOTE: Time-based filtering is now done correctly in getUnifiedBookings using getBookingEndDateTime
  // which properly handles midnight-crossing bookings (e.g., "21:00 - 00:00")

  return {
    data: activeQuery.data,
    isLoading: activeQuery.isLoading,
    isError: activeQuery.isError,
    error: activeQuery.error,
    isFetching: activeQuery.isFetching,

    // No-op pagination functions for backward compatibility
    fetchNextPage: async () => {},
    hasNextPage: false,
    isFetchingNextPage: false
  };
};
